import React, { useEffect, useState } from 'react';
import {
  Activity,
  ArrowRight,
  Baby,
  CalendarCheck,
  Check,
  ChevronDown,
  Dumbbell,
  HeartPulse,
  MapPin,
  Menu,
  Navigation,
  ShieldCheck,
  Smile,
  Snowflake,
  Sparkles,
  Star,
  Target,
  Users,
  X,
} from 'lucide-react';
import './landing.css';
import Logo from '../assets/imagens/logo_corpo_e_alma.png';
import HeroImage from '../assets/imagens/background_new.jpg';
import ImgSeparator from '../assets/imagens/separator.svg';
import ImgVenhaFazer from '../assets/imagens/venhaFazerPilates.svg';
import InstagramIcon from '../assets/icons/instagram96.svg';
import { FACEBOOK_URL, INSTAGRAM_URL, UNITS, Unit, WHATSAPP_DISPLAY } from './data';
import { openWhatsApp } from './whatsapp';
import { UnitPickerProvider, useUnitPicker } from './UnitPicker';

const NAV_LINKS = [
  { href: '#beneficios', label: 'Benefícios' },
  { href: '#modalidades', label: 'Modalidades' },
  { href: '#espaco', label: 'Espaço' },
  { href: '#depoimentos', label: 'Depoimentos' },
  { href: '#unidades', label: 'Unidades' },
  { href: '#duvidas', label: 'Dúvidas' },
];

const HIGHLIGHTS = [
  'Acompanhamento próximo',
  'Ambiente climatizado',
  'Salas amplas',
  'Melhores aparelhos do mercado',
  'Profissionais capacitados',
  'Atendimento direcionado aos seus objetivos',
];

const BENEFITS = [
  {
    icon: HeartPulse,
    title: 'Melhora a saúde',
    text: 'Mais tônus muscular, flexibilidade e uma postura que você sente no dia a dia.',
  },
  {
    icon: Activity,
    title: 'Alívio das dores',
    text: 'Você não precisa conviver com dor. Diga adeus às dores que incomodam dia e noite.',
  },
  {
    icon: Smile,
    title: 'Qualidade de vida',
    text: 'Alívio do estresse, mais disposição e autoestima lá em cima.',
  },
  {
    icon: Dumbbell,
    title: 'Desempenho',
    text: 'Otimiza suas atividades diárias e potencializa outras atividades físicas.',
  },
];

const DIFFERENTIALS = [
  {
    icon: Users,
    title: 'Acompanhamento próximo',
    text: 'Atenção de verdade: cada movimento é acompanhado e corrigido de perto.',
  },
  {
    icon: Target,
    title: 'Aula pensada para você',
    text: 'O treino é direcionado aos seus objetivos, respeitando limitações e dificuldades.',
  },
  {
    icon: Sparkles,
    title: 'Aparelhos de primeira',
    text: 'Equipamentos de alto padrão para um Pilates seguro, confortável e eficiente.',
  },
  {
    icon: Snowflake,
    title: 'Ambiente climatizado',
    text: 'Salas amplas, iluminadas e acolhedoras para você se desligar da rotina.',
  },
];

const MODALITIES = [
  {
    icon: Sparkles,
    title: 'Pilates em aparelhos',
    text: 'Exercícios em equipamentos que trabalham a conexão entre mente e corpo, fortalecendo, alongando e melhorando a consciência corporal.',
  },
  {
    icon: Baby,
    title: 'Pilates para gestantes',
    text: 'Exercícios seguros e adaptados para cada fase da gestação, respeitando as mudanças do corpo com mais conforto e confiança.',
  },
  {
    icon: ShieldCheck,
    title: 'Pilates para idosos',
    text: 'Equilíbrio, mobilidade e força para mais autonomia no dia a dia, sempre com acompanhamento próximo e ritmo adequado.',
  },
];

const TRIAL_INCLUDES = [
  'Conhecer o espaço e os aparelhos',
  'Conversar sobre seus objetivos e limitações',
  'Fazer uma aula com acompanhamento próximo',
  'Escolher o melhor horário para a sua rotina',
];

const FAQ = [
  {
    question: 'Nunca fiz Pilates. Vou conseguir acompanhar?',
    answer:
      'Com certeza. As aulas respeitam o seu ritmo, suas limitações e dificuldades. O profissional acompanha cada movimento e adapta os exercícios para você.',
  },
  {
    question: 'Gestantes e idosos podem fazer Pilates?',
    answer:
      'Sim. Temos aulas de Pilates para gestantes e para idosos, com exercícios adaptados a cada momento. Para gestantes, recomendamos a liberação do seu médico antes de começar.',
  },
  {
    question: 'Como funciona a aula experimental grátis?',
    answer:
      'Escolha a unidade (Pirituba ou Freguesia do Ó) e o WhatsApp já abre com a mensagem para aquele espaço. Combinamos o melhor horário e você vem conhecer o studio e fazer sua aula, sem compromisso.',
  },
  {
    question: 'Onde ficam as unidades?',
    answer:
      'Temos duas unidades em São Paulo: Pirituba (Av. Mutinga, 239 - Sala 04) e Freguesia do Ó (R. Bonifácio Cubas, 212 - Sala 03).',
  },
];

const useReveal = () => {
  useEffect(() => {
    const elements = document.querySelectorAll('.lp-reveal');
    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);
};

const SectionHeader: React.FC<{ eyebrow: string; title: React.ReactNode; text?: string; light?: boolean }> = ({
  eyebrow,
  title,
  text,
  light,
}) => (
  <div className={`lp-section-header lp-reveal${light ? ' lp-section-header--light' : ''}`}>
    <span className="lp-eyebrow">{eyebrow}</span>
    <img className="lp-separator" src={ImgSeparator} alt="" aria-hidden="true" />
    <h2>{title}</h2>
    {text && <p>{text}</p>}
  </div>
);

const Header: React.FC = () => {
  const openPicker = useUnitPicker();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled((wasScrolled) => (wasScrolled ? y > 12 : y > 48));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`lp-header${scrolled || menuOpen ? ' is-scrolled' : ''}`}>
      <div className="lp-container lp-header-inner">
        <a href="#inicio" className="lp-header-logo" aria-label="Studio Corpo & Alma - início">
          <img src={Logo} alt="Studio Corpo & Alma" />
        </a>

        <nav className={`lp-nav${menuOpen ? ' is-open' : ''}`}>
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href} onClick={() => setMenuOpen(false)}>
              {link.label}
            </a>
          ))}
          <button
            className="lp-btn lp-btn--primary lp-nav-cta-mobile"
            onClick={() => {
              setMenuOpen(false);
              openPicker('menu_mobile');
            }}
          >
            Agendar aula grátis
          </button>
        </nav>

        <button className="lp-btn lp-btn--primary lp-header-cta" onClick={() => openPicker('header')}>
          Agendar aula grátis
        </button>

        <button
          className="lp-menu-toggle"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
    </header>
  );
};

const Marquee: React.FC = () => (
  <div className="lp-marquee" aria-hidden="true">
    <div className="lp-marquee-track">
      {[...HIGHLIGHTS, ...HIGHLIGHTS, ...HIGHLIGHTS].map((item, index) => (
        <span key={index}>
          <Star size={14} /> {item}
        </span>
      ))}
    </div>
  </div>
);

const Hero: React.FC = () => {
  const openPicker = useUnitPicker();

  return (
    <section id="inicio" className="lp-hero">
      <img className="lp-hero-bg" src={HeroImage} alt="Sala de Pilates do Studio Corpo & Alma" />
      <div className="lp-hero-overlay" />
      <div className="lp-container lp-hero-content">
        <img className="lp-hero-brand" src={Logo} alt="Studio Corpo & Alma" />
        <h1 className="lp-hero-tag">
          <span className="lp-hero-tag-line" aria-hidden="true" />
          <span className="lp-hero-tag-text">Pilates</span>
          <span className="lp-hero-tag-line" aria-hidden="true" />
        </h1>
        <p className="lp-hero-text">
          Ambiente climatizado, aparelhos de primeira e acompanhamento próximo aos seus objetivos.
        </p>
        <button className="lp-btn lp-btn--light lp-btn--lg" onClick={() => openPicker('hero')}>
          Agende uma aula experimental grátis
        </button>
      </div>
      <Marquee />
    </section>
  );
};

const Benefits: React.FC = () => (
  <section id="beneficios" className="lp-section">
    <div className="lp-container">
      <SectionHeader
        eyebrow="Por que fazer Pilates"
        title={
          <>
            O que <em>você</em> busca?
          </>
        }
        text="Seja para acabar com as dores, ganhar força ou ter um momento só seu, o Pilates transforma a forma como você se movimenta."
      />
      <div className="lp-grid lp-grid--4">
        {BENEFITS.map(({ icon: Icon, title, text }) => (
          <article key={title} className="lp-card lp-reveal">
            <span className="lp-card-icon">
              <Icon size={26} />
            </span>
            <h3>{title}</h3>
            <p>{text}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const InviteBanner: React.FC = () => {
  const openPicker = useUnitPicker();

  return (
    <section className="lp-invite" aria-label="Venha fazer Pilates">
      <div className="lp-invite-photo">
        <img src={ImgVenhaFazer} alt="Aluna fazendo Pilates no Studio Corpo & Alma" />
      </div>
      <div className="lp-invite-board">
        <p className="lp-invite-quote">
          “O Studio Corpo & Alma foi totalmente planejado para entregar resultado aos nossos pacientes.”
        </p>
        <h2>VENHA FAZER PILATES</h2>
        <button className="lp-btn lp-btn--invite lp-btn--lg" onClick={() => openPicker('venha_fazer')}>
          Agende uma aula experimental grátis
        </button>
      </div>
    </section>
  );
};

const Differentials: React.FC = () => {
  const openPicker = useUnitPicker();

  return (
    <section className="lp-section lp-section--dark">
      <div className="lp-container lp-split">
        <div className="lp-split-media lp-reveal">
          <img src={UNITS[1].photos[0]} alt="Aparelhos de Pilates na unidade Freguesia do Ó" />
          <div className="lp-split-badge">
            <strong>2</strong>
            <span>unidades em São Paulo</span>
          </div>
        </div>
        <div className="lp-split-content">
          <SectionHeader
            light
            eyebrow="Nosso diferencial"
            title={
              <>
                Atendimento direcionado aos <em>seus objetivos</em>
              </>
            }
            text="O Studio Corpo & Alma foi totalmente planejado para entregar resultado, com atendimento direcionado aos seus objetivos."
          />
          <div className="lp-feature-list">
            {DIFFERENTIALS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="lp-feature lp-reveal">
                <span className="lp-feature-icon">
                  <Icon size={22} />
                </span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </div>
            ))}
          </div>
          <button className="lp-btn lp-btn--primary lp-btn--lg lp-reveal" onClick={() => openPicker('diferenciais')}>
            Agendar minha aula grátis <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

const Modalities: React.FC = () => (
  <section id="modalidades" className="lp-section lp-section--soft">
    <div className="lp-container">
      <SectionHeader
        eyebrow="Modalidades"
        title={
          <>
            O Pilates ideal para o <em>seu momento</em>
          </>
        }
        text="Reabilitação, condicionamento ou prevenção: criamos um plano que respeita o ritmo do seu corpo."
      />
      <div className="lp-grid lp-grid--3">
        {MODALITIES.map(({ icon: Icon, title, text }, index) => (
          <article key={title} className={`lp-modality lp-modality--${index + 1} lp-reveal`}>
            <span className="lp-modality-icon">
              <Icon size={28} />
            </span>
            <h3>{title}</h3>
            <p>{text}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const Gallery: React.FC = () => {
  const [activeId, setActiveId] = useState(UNITS[0].id);
  const activeUnit = UNITS.find((unit) => unit.id === activeId) as Unit;

  return (
    <section id="espaco" className="lp-section">
      <div className="lp-container">
        <SectionHeader
          eyebrow="Nosso espaço"
          title={
            <>
              Conheça o <em>nosso espaço</em>
            </>
          }
          text="Salas amplas, climatizadas e equipadas com os melhores aparelhos do mercado. Fotos reais das nossas unidades."
        />
        <div className="lp-tabs lp-reveal" role="tablist">
          {UNITS.map((unit) => (
            <button
              key={unit.id}
              role="tab"
              aria-selected={unit.id === activeId}
              className={`lp-tab${unit.id === activeId ? ' is-active' : ''}`}
              onClick={() => setActiveId(unit.id)}
            >
              {unit.neighborhood}
            </button>
          ))}
        </div>
        <div className={`lp-gallery lp-gallery--${activeUnit.photos.length}`} key={activeUnit.id}>
          {activeUnit.photos.map((photo, index) => (
            <figure key={photo} className="lp-gallery-item">
              <img src={photo} alt={`${activeUnit.name} - foto ${index + 1}`} loading="lazy" />
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

const Purpose: React.FC = () => (
  <section className="lp-section lp-section--soft">
    <div className="lp-container lp-purpose lp-reveal">
      <span className="lp-eyebrow">Nosso propósito</span>
      <img className="lp-separator" src={ImgSeparator} alt="" aria-hidden="true" />
      <blockquote>
        “Trazer mais qualidade de vida através da educação do movimento, para que você tenha melhora na dor e uma
        evolução completa e eficaz.”
      </blockquote>
      <div className="lp-purpose-values">
        <div>
          <h3>Conforto</h3>
          <p>Que você se sinta à vontade no nosso espaço e com nossos profissionais para progredir de verdade.</p>
        </div>
        <div>
          <h3>Apoio</h3>
          <p>Acompanhamento responsável para você executar cada movimento da forma correta e eficaz.</p>
        </div>
        <div>
          <h3>Respeito</h3>
          <p>Bons resultados respeitando suas limitações, dificuldades e o tempo do seu corpo.</p>
        </div>
      </div>
    </div>
  </section>
);

const Testimonials: React.FC = () => (
  <section id="depoimentos" className="lp-section">
    <div className="lp-container">
      <SectionHeader
        eyebrow="Depoimentos"
        title={
          <>
            Quem faz, <em>recomenda</em>
          </>
        }
        text="Veja o que nossos alunos dizem sobre a experiência no Studio Corpo & Alma."
      />
      <div className="lp-reviews lp-reveal">
        <iframe
          title="Avaliações do Studio Corpo & Alma"
          src="https://9237d98e74e04a8bafba516ac0b9b511.elf.site/"
          loading="lazy"
        />
      </div>
    </div>
  </section>
);

const UnitCard: React.FC<{ unit: Unit }> = ({ unit }) => (
  <article className="lp-unit lp-reveal">
    <div className="lp-unit-media">
      <img src={unit.photos[0]} alt={unit.name} loading="lazy" />
      <span className="lp-unit-tag">{unit.neighborhood}</span>
    </div>
    <div className="lp-unit-body">
      <h3>{unit.name}</h3>
      <p className="lp-unit-address">
        <MapPin size={18} />
        <span>
          {unit.address}
          <br />
          {unit.complement} • CEP {unit.cep}
        </span>
      </p>
      <h4>Na sua aula experimental grátis você vai:</h4>
      <ul className="lp-checklist">
        {TRIAL_INCLUDES.map((item) => (
          <li key={item}>
            <Check size={18} /> {item}
          </li>
        ))}
      </ul>
      <button className="lp-btn lp-btn--whatsapp lp-btn--lg lp-btn--block" onClick={() => openWhatsApp(unit, 'unidades')}>
        <i className="fa fa-whatsapp" aria-hidden="true" /> Agendar em {unit.neighborhood}
      </button>
      <a className="lp-unit-route" href={unit.mapsUrl} target="_blank" rel="noreferrer">
        <Navigation size={16} /> Como chegar
      </a>
      <div className="lp-unit-map">
        <iframe title={`Mapa da ${unit.name}`} src={unit.mapsEmbed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
      </div>
    </div>
  </article>
);

const Units: React.FC = () => (
  <section id="unidades" className="lp-section lp-section--units">
    <div className="lp-container">
      <SectionHeader
        light
        eyebrow="Aula experimental grátis"
        title={
          <>
            Conheça nossas <em>unidades</em>
          </>
        }
        text="Escolha a unidade mais prática para você e fale direto com a nossa equipe."
      />
      <div className="lp-grid lp-grid--2">
        {UNITS.map((unit) => (
          <UnitCard key={unit.id} unit={unit} />
        ))}
      </div>
      <p className="lp-units-note lp-reveal">
        <CalendarCheck size={18} /> Sem compromisso: você conversa com a nossa equipe antes de decidir qualquer coisa.
      </p>
    </div>
  </section>
);

const Faq: React.FC = () => (
  <section id="duvidas" className="lp-section">
    <div className="lp-container lp-container--narrow">
      <SectionHeader eyebrow="Dúvidas frequentes" title={<>Tudo o que você precisa saber</>} />
      <div className="lp-faq">
        {FAQ.map(({ question, answer }) => (
          <details key={question} className="lp-faq-item lp-reveal">
            <summary>
              {question}
              <ChevronDown size={20} />
            </summary>
            <p>{answer}</p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

const FinalCta: React.FC = () => {
  const openPicker = useUnitPicker();

  return (
    <section className="lp-final">
      <div className="lp-container lp-final-inner lp-reveal">
        <h2>Agende sua aula experimental grátis</h2>
        <p>Escolha a unidade de Pirituba ou da Freguesia do Ó e fale com a nossa equipe pelo WhatsApp.</p>
        <button className="lp-btn lp-btn--light lp-btn--lg" onClick={() => openPicker('cta_final')}>
          <i className="fa fa-whatsapp" aria-hidden="true" /> Agendar aula experimental
        </button>
      </div>
    </section>
  );
};

const Footer: React.FC = () => (
  <footer className="lp-footer">
    <div className="lp-container lp-footer-inner">
      <div className="lp-footer-brand">
        <a href="#inicio" aria-label="Studio Corpo & Alma - início">
          <img className="lp-footer-logo" src={Logo} alt="Studio Corpo & Alma" />
        </a>
        <p>Pilates com atenção de verdade, para mais saúde e bem-estar.</p>
        <div className="lp-footer-socials">
          <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" aria-label="Instagram">
            <i className="fa fa-instagram" />
          </a>
          <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" aria-label="Facebook">
            <i className="fa fa-facebook" />
          </a>
        </div>
      </div>

      <nav className="lp-footer-nav" aria-label="Navegação do rodapé">
        <span className="lp-footer-title">Navegação</span>
        {NAV_LINKS.map((link) => (
          <a key={link.href} href={link.href}>
            {link.label}
          </a>
        ))}
      </nav>

      {UNITS.map((unit) => (
        <div key={unit.id}>
          <span className="lp-footer-title">{unit.neighborhood}</span>
          <p>
            {unit.address}
            <br />
            {unit.complement}
            <br />
            CEP {unit.cep}
          </p>
          <button className="lp-footer-link" onClick={() => openWhatsApp(unit, 'rodape')}>
            <i className="fa fa-whatsapp" /> {WHATSAPP_DISPLAY}
          </button>
        </div>
      ))}
    </div>
    <div className="lp-footer-bottom">
      <div className="lp-container lp-footer-bottom-inner">
        <p>© {new Date().getFullYear()} Studio Corpo & Alma. Todos os direitos reservados.</p>
        <p>
          Desenvolvido por{' '}
          <a href="https://www.convertrex.com.br" target="_blank" rel="noreferrer">
            CONVERTREX
          </a>
        </p>
      </div>
    </div>
  </footer>
);

const FloatingWhatsApp: React.FC = () => {
  const openPicker = useUnitPicker();

  return (
    <div className="lp-float">
      <button className="lp-float-whatsapp" onClick={() => openPicker('botao_flutuante')} aria-label="Falar no WhatsApp">
        <i className="fa fa-whatsapp" aria-hidden="true" />
      </button>
      <a className="lp-float-instagram" href={INSTAGRAM_URL} target="_blank" rel="noreferrer" aria-label="Instagram">
        <img src={InstagramIcon} alt="" />
      </a>
    </div>
  );
};

const LandingContent: React.FC = () => {
  useReveal();

  return (
    <div className="lp">
      <Header />
      <Hero />
      <main>
        <Benefits />
        <Differentials />
        <Modalities />
        <InviteBanner />
        <Gallery />
        <Purpose />
        <Testimonials />
        <Units />
        <Faq />
        <FinalCta />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export const Landing: React.FC = () => (
  <UnitPickerProvider>
    <LandingContent />
  </UnitPickerProvider>
);

export default Landing;
